import type { Rect } from '../core/types';

export type TextureFormat = 'rgba8' | 'r8' | 'rgba16f';
export type TextureFilter = 'nearest' | 'linear';

interface FormatSpec {
  internalFormat: number;
  format: number;
  type: number;
  channels: number;
  bytesPerPixel: number;
}

/**
 * Texture rows run top-to-bottom in document space: row 0 is the top row of the
 * image. Offscreen passes therefore draw the unit quad with `y` *not* flipped
 * (see `QUAD_TO_TARGET` in `quad.ts`), and `readPixels` yields rows in the same
 * order that `ImageData` expects.
 */
export class Texture {
  readonly handle: WebGLTexture;
  readonly format: TextureFormat;
  private readonly gl: WebGL2RenderingContext;
  private readonly spec: FormatSpec;
  private w: number;
  private h: number;

  private constructor(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    format: TextureFormat,
    filter: TextureFilter,
  ) {
    this.gl = gl;
    this.format = format;
    this.spec = formatSpec(gl, format);
    this.w = width;
    this.h = height;
    this.handle = gl.createTexture()!;

    gl.bindTexture(gl.TEXTURE_2D, this.handle);
    const glFilter = filter === 'linear' ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, glFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, glFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.allocate(width, height);
  }

  static create(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    format: TextureFormat = 'rgba8',
    filter: TextureFilter = 'linear',
  ): Texture {
    return new Texture(gl, Math.max(1, width | 0), Math.max(1, height | 0), format, filter);
  }

  get width(): number {
    return this.w;
  }

  get height(): number {
    return this.h;
  }

  get channels(): number {
    return this.spec.channels;
  }

  get bytes(): number {
    return this.w * this.h * this.spec.bytesPerPixel;
  }

  bind(unit = 0): void {
    this.gl.activeTexture(this.gl.TEXTURE0 + unit);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.handle);
  }

  resize(width: number, height: number): void {
    const w = Math.max(1, width | 0);
    const h = Math.max(1, height | 0);
    if (w === this.w && h === this.h) return;
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.handle);
    this.allocate(w, h);
  }

  /** Replaces the whole texture with an image, canvas or bitmap. */
  uploadSource(source: TexImageSource): void {
    const { gl, spec } = this;
    gl.bindTexture(gl.TEXTURE_2D, this.handle);
    gl.texImage2D(gl.TEXTURE_2D, 0, spec.internalFormat, spec.format, spec.type, source);
    const width = 'width' in source ? Number(source.width) : this.w;
    const height = 'height' in source ? Number(source.height) : this.h;
    this.w = width;
    this.h = height;
  }

  /** Uploads a tightly packed region. `data.length` must be `w * h * channels`. */
  uploadPixels(rect: Rect, data: ArrayBufferView): void {
    const { gl, spec } = this;
    if (rect.width <= 0 || rect.height <= 0) return;
    gl.bindTexture(gl.TEXTURE_2D, this.handle);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      spec.format,
      spec.type,
      data,
    );
  }

  /** Reads a region back to the CPU. Slow — only for history, export and flood fill. */
  readPixels(rect?: Rect): Uint8Array {
    const { gl, spec } = this;
    const r = rect ?? { x: 0, y: 0, width: this.w, height: this.h };
    const out = new Uint8Array(Math.max(1, r.width * r.height * spec.channels));
    if (r.width <= 0 || r.height <= 0) return out;

    const fbo = getReadFramebuffer(gl);
    const previous = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.handle, 0);
    gl.readPixels(r.x, r.y, r.width, r.height, spec.format, spec.type, out);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, null, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, previous);
    return out;
  }

  dispose(): void {
    this.gl.deleteTexture(this.handle);
  }

  private allocate(width: number, height: number): void {
    const { gl, spec } = this;
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      spec.internalFormat,
      width,
      height,
      0,
      spec.format,
      spec.type,
      null,
    );
    this.w = width;
    this.h = height;
  }
}

function formatSpec(gl: WebGL2RenderingContext, format: TextureFormat): FormatSpec {
  if (format === 'r8') {
    return {
      internalFormat: gl.R8,
      format: gl.RED,
      type: gl.UNSIGNED_BYTE,
      channels: 1,
      bytesPerPixel: 1,
    };
  }
  // Signed intermediates: eight bits per channel round on every pass, and an
  // iterative solve turns that rounding into a visible constant offset.
  if (format === 'rgba16f') {
    return {
      internalFormat: gl.RGBA16F,
      format: gl.RGBA,
      type: gl.HALF_FLOAT,
      channels: 4,
      bytesPerPixel: 8,
    };
  }
  return {
    internalFormat: gl.RGBA8,
    format: gl.RGBA,
    type: gl.UNSIGNED_BYTE,
    channels: 4,
    bytesPerPixel: 4,
  };
}

const readFramebuffers = new WeakMap<WebGL2RenderingContext, WebGLFramebuffer>();

function getReadFramebuffer(gl: WebGL2RenderingContext): WebGLFramebuffer {
  let fbo = readFramebuffers.get(gl);
  // A restored context reuses the same `gl` object while invalidating every
  // object created before the loss, so the cached handle has to be re-checked.
  if (!fbo || !gl.isFramebuffer(fbo)) {
    fbo = gl.createFramebuffer()!;
    readFramebuffers.set(gl, fbo);
  }
  return fbo;
}

/** Drops the cached read framebuffer, e.g. after a context loss. */
export function invalidateReadFramebuffer(gl: WebGL2RenderingContext): void {
  readFramebuffers.delete(gl);
}
